import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  IconButton,
  Accordion, AccordionSummary, AccordionDetails, Grid,
  Typography, Button
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import AddBoxIcon from '@material-ui/icons/AddBox'
import AddNewDialog from '../components/Dialog/AddNewDialog'
import { LanguageConsumer } from '../context/LanguageContext'
import Moment from 'react-moment'
import EditIcon from '@material-ui/icons/Edit'
import DeleteForeverIcon from '@material-ui/icons/DeleteForever'
import AddNewForm from './EntityAddForm'
import { ItemsListConsumer, ItemsListProvider } from '../context/ItemsListContext'
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight'

const FormListAddMore = ({ classes, labelKey, entityName, updateParentEntity, list, children, netTotal, totalTaxableFees, calculatedFees, valueDifference, currency, locale, inline, noEdit }) => {
  const intl = useIntl()
  const getColumnValue = (key, value) => {
    if (!!key) {
      switch (key) {
        case 'start_date':
        case 'end_date':
          return <LanguageConsumer>
            {({ locale }) => (
              <Moment locale={locale === 'ar' ? 'ar-ly' : 'en-us'}
                      format="DD MMMM YYYY, h:mm A">{value}</Moment>
            )}
          </LanguageConsumer>

        case 'activeFrom':
        case 'activeTo':
          return <Moment className={'noWrap'} locale={locale === 'ar' ? 'ar-sa' : 'en-us'}
                         format={locale === 'ar' ? 'YYYY/MM/DD' : 'DD/MM/YYYY'}>{value}</Moment>

        case 'codeCategorization':
          return Object.keys(value || {})?.map((catLevel, index) => (
            !!value[catLevel]?.name &&
            <div key={index}
                 className={classes.category + ' ' + catLevel}>
              {catLevel !== 'level1' &&
              <SubdirectoryArrowRightIcon className={locale === 'ar' ? 'flip-direction' : ''}/>}{locale === 'ar' ? value[catLevel]?.nameAr || value[catLevel]?.name : value[catLevel]?.name}
            </div>
          )) || ''

        case 'active':
          return <span
            className={classes.codeStatus + (!!value ? ' active' : ' not-active')}>{!!value ? intl.formatMessage({ id: 'Fields.label.view.codeActive.true' }) : intl.formatMessage({ id: 'Fields.label.view.codeActive.false' })}</span>

        default:
          return value
      }
    } else {
      return ''
    }
  }
  return (
    <ItemsListProvider entityName={entityName} netTotal={netTotal} totalTaxableFees={totalTaxableFees} listArr={list}
                       calculatedFees={calculatedFees} valueDifference={valueDifference}
                       all currency={currency} locale={locale} onListUpdate={updateParentEntity}>
      <ItemsListConsumer>
        {({ listingData, dialogActionSubmit, currentSelectedRow, currentDialogAction, isDialogOpen, cancelDialog, openDialog, listingHeader, updateCurrentSelectedItem }) => (
          <div className={classes.FormRow + ' ' + classes.FormRowList}>
            <div className={classes.FormListHeader + (!!inline ? ' inline' : '')}>
              <Typography variant="h5" component="h3"><FormattedMessage id={labelKey}/></Typography>
              {!!inline ?
                <AddNewForm entityName={entityName}
                            currentData={currentSelectedRow}
                            totalTaxableFees={totalTaxableFees}
                            calculatedFees={calculatedFees}
                            valueDifference={valueDifference}
                            netTotal={netTotal}
                            currency={currency}
                            updateParentEntity={updateParentEntity}
                            onSubmit={dialogActionSubmit}
                            currentAction={currentDialogAction}
                            cancelDialog={cancelDialog}/>
                :
                <>
                  <Button color='primary' variant='contained'
                          onClick={(e) => {
                            !!e?.preventDefault && e.preventDefault()
                            !!e?.stopPropagation && e.stopPropagation()
                            openDialog()
                          }}
                  >
                    <AddBoxIcon/>
                    <FormattedMessage id={`EntityList.${entityName}.dialogTitle.create`}/>
                  </Button>
                  <AddNewDialog
                    children={<AddNewForm entityName={entityName}
                                          currentData={currentSelectedRow}
                                          totalTaxableFees={totalTaxableFees}
                                          calculatedFees={calculatedFees}
                                          valueDifference={valueDifference}
                                          netTotal={netTotal}
                                          currency={currency}
                                          updateParentEntity={updateParentEntity}
                                          onSubmit={dialogActionSubmit}
                                          currentAction={currentDialogAction}
                                          cancelDialog={cancelDialog}/>}
                    dialogAction={currentDialogAction}
                    DialogTitle={<FormattedMessage
                      id={`EntityList.${entityName}.dialogTitle.${currentDialogAction}`}
                      values={{ name: currentSelectedRow?.name || currentSelectedRow?.id || '' }}/>}
                    isDialogOpen={isDialogOpen}
                    cancelDialog={cancelDialog}
                  />
                </>
              }
            </div>
            {!!listingData?.length &&
            <div className={classes.EntityListContainer}>
              {listingData?.map((item) => (
                <Accordion key={item.id}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon/>}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <div className={classes.ItemHeadingWrapper}>
                      <Typography className={classes.ItemHeading}>{item?.icon}{item?.headerTitle}</Typography>

                      <div className={classes.EntityActionsWrapper}>
                        {!noEdit &&
                        <IconButton color="secondary"
                                    aria-label={intl.formatMessage({ id: 'EntityList.dialogTitle.edit' })}
                                    onClick={(e) => {
                                      !!e?.preventDefault && e.preventDefault()
                                      !!e?.stopPropagation && e.stopPropagation()
                                      openDialog('edit', item, item.id)
                                    }}>
                          <EditIcon/>
                        </IconButton>
                        }
                        <IconButton color="secondary"
                                    aria-label={intl.formatMessage({ id: 'EntityList.dialogTitle.delete' })}
                                    onClick={(e) => {
                                      !!e?.preventDefault && e.preventDefault()
                                      !!e?.stopPropagation && e.stopPropagation()
                                      openDialog('delete', item, item.id, null, updateParentEntity)
                                    }}>
                          <DeleteForeverIcon/>
                        </IconButton>
                      </div>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className={classes.EntityInfoContainer}>
                      <div className={classes.EntityInfoWrapper}>
                        {listingHeader.map((column, index) => (
                          !!item[column.id] && !['icon', 'headerTitle', 'actions'].includes(column.id) &&
                          <Grid item xs={12} sm={6} md={6} key={index} className={classes.infoItemWrapper}>
                            <Grid item xs={12} sm={5} className={classes.infoItemLabel}>
                              {!!column.label && <FormattedMessage id={column.label}/>}
                            </Grid>
                            <Grid item xs={12} sm={7} className={classes.infoItemValue}>
                              {getColumnValue(column.id, item[column.id])}
                            </Grid>
                          </Grid>
                        ))}
                      </div>
                      {children}
                    </div>
                  </AccordionDetails>
                </Accordion>
              ))}
            </div>
            }

          </div>
        )}
      </ItemsListConsumer>
    </ItemsListProvider>
  )
}

export default FormListAddMore
